import { Button, Checkbox } from '@design-system';
import { Trans, useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';

import { pathFor } from '../../../helpers/links';
import { useCalculateOrderPricing } from '../../../hooks/useCalculateOrderPricing';
import { useOnBfCacheLoad } from '../../../hooks/useOnBfCacheLoad';
import { useAuth } from '../../../providers';
import { Account } from '../../../types/Account';
import {
  GeneratedAccount,
  GeneratedCalculateOrderPricingInputItem,
  GeneratedPrivateLink,
} from '../../../types/generated';
import { ProductV2Storyblok } from '../../../types/generated-storyblok';
import {
  DrawerFormType,
  ProductPurchaseDetails,
  ProductPurchasingController,
} from '../../../types/Purchasing';
import { AnalyticsEmitter } from '../../../utils/analytics/emitter';
import { framedByDefault } from '../../../utils/frames';
import { ProductDispatchDate } from '../../ProductDispatchDate/ProductDispatchDate';
import { ProductPrice } from '../../ProductPrice/ProductPrice';
import UnderlinedLink from '../../UnderlinedLink';
import EmbeddedContainer from '../DrawerContainer/EmbeddedContainer';
import FrameSelector from './FrameSelector';
import { ReviewItem } from './ReviewItem';
import ReviewSummary, { Line } from './ReviewSummary';
import { ShippingSelector } from './ShippingSelector';

type Props<T extends ProductPurchaseDetails> = {
  analyticsEmitter?: AnalyticsEmitter;
  drawerType: 'draw' | 'checkout';
  privateLink?: GeneratedPrivateLink;
  product: ProductType;
  purchasingController: ProductPurchasingController<T>;
  setVisibleForm?: (formType: DrawerFormType) => void;
  shown: boolean;
  showTitle?: boolean;
};

export const getResaleAgreementUrl = ({
  address,
  artistName,
  name,
  productName,
}: {
  name: string | undefined;
  productName: string | undefined;
  artistName: string | undefined;
  address: string | undefined;
}) => {
  if (!name) return;

  const params = {
    name,
    address,
    works: `${productName} by ${artistName}`,
  };

  // Remove undefined values
  const cleanedParams = Object.entries(params)
    .filter(([_, value]) => value !== undefined)
    .reduce(
      (obj, [key, value]) => {
        if (value) obj[key] = value;
        return obj;
      },
      {} as Record<string, string>,
    );

  return pathFor({
    page: 'RESALE_AGREEMENT',
    queryParams: new URLSearchParams(cleanedParams).toString(),
  });
};

const ReviewForm: React.FC<Props<ProductPurchaseDetails>> = ({
  analyticsEmitter,
  drawerType,
  product: storyblokProduct,
  purchasingController,
  setVisibleForm,
  showTitle = true,
  shown,
}) => {
  const { t } = useTranslation(['product', 'countries']);
  const { checkoutLoading, frame, goToCheckout, product, quantity } =
    purchasingController;
  const [itemsToBuy, setItemsToBuy] = useState<
    GeneratedCalculateOrderPricingInputItem[]
  >([]);

  const { calculation: orderPriceCalculation } = useCalculateOrderPricing({
    items: itemsToBuy,
  });

  const [resaleChecked, setResaleChecked] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const hasResaleAgreement = !purchasingController.product?.noResaleAgreement;
  const frameIncluded = framedByDefault(
    purchasingController.product?.framingOffered,
  );

  useEffect(() => {
    if (shown) {
      analyticsEmitter?.('', 'viewed', {
        'product_frame_glazing': frame?.title,
      });
    }
  }, [shown]);

  useOnBfCacheLoad(() => {
    setLoading(false);
  });

  useEffect(() => {
    if (!product) return;

    const items = [];

    if (purchasingController.collectionSet) {
      items.push(
        ...purchasingController.collectionSet
          .filter(({ selected }) => selected)
          .map((release) => ({
            variantId: release.shopifyVariantId,
            quantity: release.quantity,
          })),
      );
    } else {
      items.push({
        variantId: product.shopifyVariantId!,
        quantity,
      });
    }

    if (frame) {
      frame?.variant.consolidatedVariants.forEach((variant) => {
        items.push({
          variantId: variant.shopifyFrameVariantId,
          quantity: variant.quantity,
        });
      });
    }

    setItemsToBuy(items);
  }, [product, quantity, purchasingController.collectionSet, frame]);

  const embedded = drawerType === 'draw';

  const userName =
    (user as GeneratedAccount)?.fullName ??
    `${(user as Account)?.firstName} ${(user as Account)?.lastName}`;

  const artistKey =
    storyblokProduct.component === 'product_v2' ? 'artists' : 'productArtist';

  const resaleAgreementUrl =
    hasResaleAgreement &&
    getResaleAgreementUrl({
      name: userName,
      productName: purchasingController.product?.name,
      // @ts-expect-error product v2 upgrade needs to replace ProductType
      artistName: (storyblokProduct[artistKey][0] as ResolvedRelatedArtist)
        .artist.name,
      address: t(`countries:${user?.countryCode!}`),
    });

  return (
    <EmbeddedContainer embedded={embedded}>
      <div className="flex flex-col space-y-6 text-neutral-6">
        {showTitle && (
          <div className="col-span-12 hidden md:block">
            <p className="text-md font-bold md:text-lg">
              {t(`drawers.review.${drawerType}.title`)}
            </p>
          </div>
        )}
        {drawerType === 'draw' && (
          <div className="col-span-12">
            <p>{t(`drawers.review.${drawerType}.subTitle`)}</p>
          </div>
        )}
        <div className="grid-section col-span-12 w-full gap-y-6">
          <div className="col-span-12 flex flex-col rounded-sm border border-divider/20 px-3 pb-3 pt-4 md:px-4 md:pb-4 md:pt-4">
            <div className="flex flex-col gap-4">
              {purchasingController.collectionSet ? (
                purchasingController.collectionSet
                  .filter(({ selected }) => selected)
                  .map((release) => (
                    <ReviewItem
                      key={release.shopifyId}
                      allowMultiples={release.allowMultipleProducts}
                      allowMultiplesLimit={release.allowMultipleProductsLimit}
                      artist={release.artist}
                      disabled={loading}
                      imageUrl={release.image}
                      price={release.price}
                      quantity={release.quantity}
                      title={release.title}
                      updateQuantity={(value) => {
                        purchasingController.collectionSetUpdateQuantity(
                          release.shopifyId,
                          value,
                        );
                      }}
                    />
                  ))
              ) : (
                <ReviewItem
                  allowMultiples={
                    purchasingController.product?.allowMultipleProducts ?? false
                  }
                  allowMultiplesLimit={
                    purchasingController.product?.maximumSalesCapacity ||
                    Number.MAX_SAFE_INTEGER
                  }
                  artist={purchasingController.product?.artist ?? ''}
                  disabled={loading}
                  imageFocus={
                    storyblokProduct.component === 'product_v2'
                      ? (storyblokProduct as unknown as ProductV2Storyblok)
                          .ogImage?.focus ?? ''
                      : storyblokProduct.thumbnail?.focus ?? ''
                  }
                  imageUrl={
                    storyblokProduct.component === 'product_v2'
                      ? (storyblokProduct as unknown as ProductV2Storyblok)
                          .ogImage.filename
                      : storyblokProduct.thumbnail.filename
                  }
                  price={purchasingController.product?.price ?? 0}
                  quantity={purchasingController.quantity}
                  title={purchasingController.product?.name ?? ''}
                  updateQuantity={(value) => {
                    if (value < purchasingController.quantity)
                      analyticsEmitter?.('remove', 'clicked');

                    purchasingController.setQuantity(value);
                  }}
                />
              )}
            </div>
            {!frameIncluded && purchasingController.frameOptions?.length ? (
              <div className="col-span-12 pt-3 md:pt-4">
                <div className="col-span-12 border-t border-divider/20 pt-3 md:pt-4">
                  <FrameSelector
                    product={storyblokProduct}
                    purchasingController={purchasingController}
                    setVisibleForm={(formType) => {
                      if (formType === 'frame')
                        analyticsEmitter?.('framing', 'clicked');

                      setVisibleForm?.(formType);
                    }}
                  />
                </div>
              </div>
            ) : null}
            {!(
              purchasingController.frameOptions &&
              purchasingController.frameOptions.length > 0
            ) &&
              frameIncluded && (
                <div className="col-span-12 pt-3 md:pt-4">
                  <div className="cursor-default border-t border-divider/20 px-3 pt-3 md:px-4 md:pt-4">
                    <div className="flex items-center justify-between">
                      {t('drawers.review.includedFrameTitle')}
                      <div className="flex">
                        <div className="text-neutral-4">
                          {t('drawers.review.includedFrameState')}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
          </div>
          <div className="col-span-12">
            <ShippingSelector
              setVisibleForm={(formType) => {
                if (formType === 'locale')
                  analyticsEmitter?.('shipping', 'clicked');

                setVisibleForm?.(formType);
              }}
            />
          </div>

          <div className="col-span-12">
            <ProductDispatchDate
              dispatchDate={purchasingController.dispatchDate}
              variant="estimation"
            />
          </div>

          {(purchasingController.product?.allowMultipleProducts ||
            purchasingController.product?.shippingRequired ||
            !purchasingController.product?.noTaxCharged) && (
            <div
              className={`col-span-12 ${
                drawerType === 'draw' ? 'pb-3 md:pb-4' : ''
              }`}
            >
              <ReviewSummary
                orderPriceCalculation={orderPriceCalculation}
                product={product || undefined}
              />
            </div>
          )}
          {drawerType === 'draw' && (
            <div className="col-span-12 border-t border-divider/20 pt-3 md:pt-4">
              <div className="flex flex-col">
                <Line
                  title={t(`drawers.review.${drawerType}.todayDue`)}
                  value="0"
                />

                {orderPriceCalculation && orderPriceCalculation.totalPrice && (
                  <Line
                    className="font-bold"
                    title={t(`drawers.review.${drawerType}.winDue`)}
                    value={
                      <ProductPrice
                        inMinorUnit
                        price={orderPriceCalculation.totalPrice}
                        skipConvertCurrency
                        skipTaxCalculation
                      />
                    }
                  />
                )}
              </div>
            </div>
          )}
        </div>
        {purchasingController.product?.noOrderCancellation && (
          <div className="col-span-12 flex flex-col text-neutral-4">
            <span className="text-neutral-4">
              {t(`drawers.review.${drawerType}.note`)}
            </span>
          </div>
        )}
        {drawerType === 'draw' &&
          user &&
          resaleAgreementUrl &&
          storyblokProduct.productArtist.length > 0 && (
            <div className="col-span-12 flex flex-col">
              <Checkbox
                checked={resaleChecked}
                data-testid="field-accept-resale-agreement"
                disabled={loading}
                id="accept-resale-agreement"
                label={
                  (
                    <Trans
                      i18nKey={`product.drawers.review.${drawerType}.resaleCheckbox`}
                      t={t}
                    >
                      I have read and accept the&nbsp;
                      <UnderlinedLink href={resaleAgreementUrl} target="_blank">
                        Resale Agreement
                      </UnderlinedLink>
                      , which outlines that if I win the draw, I cannot resell
                      this artwork for a period of 2 years.
                    </Trans>
                  ) as any
                }
                name="accept-resale-agreement"
                onChange={(e) => {
                  setResaleChecked(e.currentTarget.checked);
                }}
                type="checkbox"
                value="accept-resale-agreement"
              />
            </div>
          )}
        <div
          className="col-span-12 pt-6"
          data-testid="review-form-checkout-cta"
        >
          <Button
            disabled={
              drawerType === 'draw' && hasResaleAgreement && !resaleChecked
            }
            fullWidth
            label={t(`drawers.review.${drawerType}.cta`)}
            loading={loading || checkoutLoading}
            onClick={() => {
              setLoading(true);

              try {
                (async () => {
                  analyticsEmitter?.('checkout', 'clicked', {
                    'product_frame_chosen': Boolean(frame),
                    'product_frame_glazing': frame?.variant.title,
                  });
                  goToCheckout();
                })();
              } finally {
                setLoading(false);
              }
            }}
            variant="primary"
          />
        </div>
      </div>
    </EmbeddedContainer>
  );
};

export default ReviewForm;
